import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    coaTemplateApplyEventDetailQueryOptions,
    coaTemplateApplyEventEligibilityQueryOptions,
    coaTemplateApplyEventQueryKeys,
} from "@api/coaTemplateApplyEvent.api";
import {
    CoaTemplateActivityEventRef,
    CoaTemplateActivityEventResult,
    loadCoaTemplateActivityEvents,
} from "./coaTemplateActivityEvents";

// Loads every event behind one aggregate query, but routes each request through
// the canonical per-event detail/eligibility query keys. The rail stays bounded
// (no fan-out of one request per entity), while single-event consumers and later
// invalidation both hit the same cache entries the aggregate filled.
export function loadCoaTemplateActivityEventsViaCache({
    eventRefs,
    queryClient,
    concurrency,
}: {
    eventRefs: CoaTemplateActivityEventRef[];
    queryClient: QueryClient;
    concurrency?: number;
}): Promise<CoaTemplateActivityEventResult[]> {
    return loadCoaTemplateActivityEvents({
        eventRefs,
        concurrency,
        loadDetail: (eventRef) =>
            queryClient.fetchQuery(
                coaTemplateApplyEventDetailQueryOptions({
                    workspaceId: eventRef.workspaceId,
                    eventId: eventRef.eventId,
                }),
            ),
        loadEligibility: (eventRef) =>
            queryClient.fetchQuery(
                coaTemplateApplyEventEligibilityQueryOptions({
                    workspaceId: eventRef.workspaceId,
                    eventId: eventRef.eventId,
                }),
            ),
    });
}

export function useCoaTemplateActivityEvents({
    eventRefs,
    enabled,
}: {
    eventRefs: CoaTemplateActivityEventRef[];
    enabled: boolean;
}) {
    const queryClient = useQueryClient();

    return useQuery({
        queryKey: coaTemplateApplyEventQueryKeys.activity(eventRefs),
        queryFn: () =>
            loadCoaTemplateActivityEventsViaCache({ eventRefs, queryClient }),
        enabled: enabled && eventRefs.length > 0,
    });
}
